<template>
  <button :class="buttonClasses">
    <slot />
    <v-progress-circular
      v-if="isLoading"
      indeterminate
      size="20"
      color="currentcolor"
    ></v-progress-circular>
  </button>
</template>

<script>
import SxIcon from '@/components/sx/SxIcon';

export default {
  props: {
    type: {
      type: String, // 'primary' | 'secondary' | 'error'
      required: true,
    },
    value: {
      type: String,
      required: false,
    },
    isDisabled: {
      type: Boolean,
      default: false,
      required: false,
    },
    isLoading: {
      type: Boolean,
      default: false,
      required: false,
    },
    isActive: {
      type: Boolean,
      default: false,
      required: false,
    },
    isLeft: {
      type: Boolean,
      default: false,
      required: false,
    },
    isRight: {
      type: Boolean,
      default: false,
      required: false,
    },
    size: {
      type: String,
      required: false, // tiny | small
    },
  },
  components: {
    SxIcon,
  },
  computed: {
    buttonClasses() {
      const classes = new Set(['button']);

      const type = this.type || 'primary';
      const size = this.size || 'normal';

      // Types
      if (type === 'primary') classes.add('button--primary');
      if (type === 'secondary') classes.add('button--secondary');
      if (type === 'error') classes.add('button--error');

      // Sizes
      if (size === 'normal') classes.add('button--size-normal');
      if (size === 'small') classes.add('button--size-small');
      if (size === 'tiny') classes.add('button--size-tiny');
      if (size === 'micro') classes.add('button--size-micro');

      // States
      if (this.isDisabled) classes.add('button--disabled');
      if (this.isLoading) classes.add('button--loading');
      if (this.isActive) classes.add('button--active');

      // Joined
      if (this.isLeft) classes.add('button--joined-left');
      if (this.isRight) classes.add('button--joined-right');

      return [...classes].join(' ');
    },
  },
};
</script>

<style scoped>
.button {
  position: relative;

  align-items: center;
  display: flex;

  border: 0;
  border-radius: 8px;
  box-shadow: 0px 1px 1px 0px rgba(114, 113, 125, 0.1);
  outline: none;
  transition: all 80ms ease-in-out;

  font-family: var(--font-family-sans);
  font-weight: 500;
  line-height: 40px;
  cursor: pointer;
  white-space: nowrap;
}

.button:hover {
  z-index: 1;
}

/* Types */
.button--primary {
  background: linear-gradient(180deg, var(--blue-500) 0%, var(--blue-600) 200%);
  color: white;
}

.button--primary:hover {
  background: linear-gradient(180deg, var(--blue-700) 0%, var(--blue-700) 200%);
}

.button--primary:focus-visible {
  box-shadow: 0 0 0 3px var(--blue-200);
}

.button--error {
  border: 1px solid var(--red-200);
  background: linear-gradient(180deg, var(--gray-0) 0%, var(--red-100) 200%);
  color: var(--red-500);
}

.button--error:hover {
  border: 1px solid var(--red-300);
}

.button--error:focus-visible {
  border-color: var(--red-300);
  box-shadow: 0 0 0 3px var(--red-100);
}

.button--secondary {
  border: 1px solid var(--blue-200);
  background: linear-gradient(180deg, var(--gray-0) 0%, var(--blue-100) 200%);
  color: var(--blue-700);
}

.button--secondary:hover {
  border: 1px solid var(--blue-300);
}

.button--secondary:focus-visible {
  border-color: var(--blue-300);
  box-shadow: 0 0 0 3px var(--blue-100);
}

.button--secondary.button--active,
.button--secondary:active {
  background: linear-gradient(180deg, var(--blue-200) 0%, var(--blue-100) 200%);
  box-shadow: inset 0 1px 2px 0 var(--blue-200);
}

/* Sizes */
.button--size-normal {
  gap: 6px;
  height: 40px;
  padding: 0 14px;

  font-size: 16px;
}

.button--size-small {
  gap: 4px;
  height: 36px;
  padding: 0 10px;

  font-size: 14px;
}

.button--size-tiny {
  gap: 3px;
  height: 32px;
  padding: 0 6px;

  font-size: 12px;
}

.button--size-micro {
  gap: 2px;
  height: 24px;
  padding: 0 4px;

  font-size: 12px;
}

/* States */
.button--loading,
.button--disabled {
  opacity: 0.6;

  pointer-events: none;
  cursor: default;
}

/* Joined */
.button--joined-left {
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
}

.button--joined-right {
  margin-left: -1px;

  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
}
</style>
